/**
 * Marketing › Attribution's source (card 3d9c00e6): `GET /api/v1/analytics/attribution/conversions`
 * (docs/architecture/attribution.md §6). `analytics.read`.
 *
 * TmT first-party attributed purchases, revenue, new customers and refunds under one
 * model, grouped by campaign, source / medium or a Meta level. **Production only, all
 * websites**: the route takes no `environment` or `website_id`, so the global filter
 * bar's environment and website cannot narrow it. Every row is `tmtAttributed` under the
 * named `model`; Meta's reported spend rides beside it at the Meta levels, labelled by
 * `spendSource`, never folded into the TmT metrics (blueprint 06 §6.10). No ratio is
 * served — CPA and ROAS are the caller's, from these sums.
 *
 * Before the coverage card (wave 20 B1) the unattributed row (`id: null`) was always
 * last: orders with no touch in the lookback, sharing the all-null row with direct
 * touches. Since, every row says its `credit`: the null-id `touch` row holds direct and
 * untagged touches, and the **Not tracked** row (`credit: 'unattributed'`, `key: null`)
 * is the orders with no tracked touch, always last and never merged with it. The report
 * carries the `data_quality` block (`./dataQuality`). Both are absent from an older API.
 */

import { dataQualityFromWire, type ConversionCredit, type DataQuality, type DataQualityOut } from './dataQuality'
import type { AttributionModel } from './types'

export const ATTRIBUTION_GROUP_BYS = ['campaign', 'source_medium', 'ad_campaign', 'ad_set', 'ad'] as const
export type AttributionGroupBy = (typeof ATTRIBUTION_GROUP_BYS)[number]

export const ATTRIBUTION_GROUP_BY_LABELS: Record<AttributionGroupBy, string> = {
  campaign: 'Campaign',
  source_medium: 'Source / medium',
  ad_campaign: 'Meta campaign',
  ad_set: 'Meta ad set',
  ad: 'Meta ad',
}

/** The groupings that are Meta hierarchy levels — the only ones that carry spend (`schemas.META_LEVELS`). */
export const META_GROUP_BYS: ReadonlySet<AttributionGroupBy> = new Set(['ad_campaign', 'ad_set', 'ad'])

export interface AttributionQuery {
  from: string
  to: string
  model: AttributionModel
  groupBy: AttributionGroupBy
  /** The previous period of the same length; the API has no year-on-year comparison. */
  compare: boolean
}

/** TmT first-party counters, in minor units of the report's currency where money. */
export interface AttributionMetrics {
  purchases: number
  revenueMinor: number
  newCustomers: number
  refunds: number
  refundMinor: number
}

export interface AttributionRow {
  /** The campaign, `source / medium` or the Suite's Meta object id; null only for the unattributed row. */
  id: string | null
  name: string | null
  /** The grouped dimensions by column name (`campaign`; `source`/`medium`; `ad_campaign_id`…); null on the Not tracked row. */
  key: Record<string, string | null> | null
  /** Absent from an API older than the coverage card, which did not split the Not tracked row out. */
  credit?: ConversionCredit
  model: AttributionModel
  tmtAttributed: true
  metrics: AttributionMetrics
  /** The previous period's figures; null when the report was not compared. */
  previous: AttributionMetrics | null
  /** Meta-reported spend for the same object and days; null unless grouped by a Meta level. */
  spendMinor: number | null
  previousSpendMinor: number | null
}

export interface AttributionReport {
  start: string
  end: string
  model: AttributionModel
  groupBy: AttributionGroupBy
  compared: boolean
  previousStart: string | null
  previousEnd: string | null
  currency: string
  lookbackDays: number
  tmtAttributed: true
  /** Where `spendMinor` comes from; null when the grouping carries no spend. */
  spendSource: 'meta' | null
  rows: AttributionRow[]
  totals: AttributionMetrics
  totalsPrevious: AttributionMetrics | null
  totalSpendMinor: number | null
  totalPreviousSpendMinor: number | null
  /** The tracking baseline and coverage for this range and comparison; null from an older API. */
  dataQuality: DataQuality | null
}

export interface AttributionApi {
  attributionConversions(query: AttributionQuery): Promise<AttributionReport>
}

// --- wire shapes (snake_case, as the API sends them) -------------------------

export interface AttributionMetricsOut {
  purchases: number
  revenue_minor: number
  new_customers: number
  refunds: number
  refund_minor: number
}

export interface AttributionRowOut {
  id: string | null
  name: string | null
  key: Record<string, string | null> | null
  credit?: ConversionCredit
  model: AttributionModel
  tmt_attributed: true
  metrics: AttributionMetricsOut
  previous: AttributionMetricsOut | null
  spend_minor: number | null
  previous_spend_minor: number | null
}

export interface AttributionConversionsOut {
  start: string
  end: string
  model: AttributionModel
  group_by: AttributionGroupBy
  compare: 'previous_period' | null
  previous_start: string | null
  previous_end: string | null
  currency: string
  lookback_days: number
  tmt_attributed: true
  spend_source: 'meta' | null
  rows: AttributionRowOut[]
  totals: AttributionMetricsOut
  totals_previous: AttributionMetricsOut | null
  total_spend_minor: number | null
  total_previous_spend_minor: number | null
  data_quality?: DataQualityOut | null
}

export function attributionMetricsFromWire(body: AttributionMetricsOut): AttributionMetrics {
  return {
    purchases: body.purchases,
    revenueMinor: body.revenue_minor,
    newCustomers: body.new_customers,
    refunds: body.refunds,
    refundMinor: body.refund_minor,
  }
}

export function attributionRowFromWire(row: AttributionRowOut): AttributionRow {
  return {
    id: row.id,
    name: row.name,
    key: row.key,
    ...(row.credit ? { credit: row.credit } : {}),
    model: row.model,
    tmtAttributed: row.tmt_attributed,
    metrics: attributionMetricsFromWire(row.metrics),
    previous: row.previous ? attributionMetricsFromWire(row.previous) : null,
    spendMinor: row.spend_minor ?? null,
    previousSpendMinor: row.previous_spend_minor ?? null,
  }
}

export function attributionFromWire(body: AttributionConversionsOut): AttributionReport {
  return {
    start: body.start,
    end: body.end,
    model: body.model,
    groupBy: body.group_by,
    compared: body.compare !== null,
    previousStart: body.previous_start,
    previousEnd: body.previous_end,
    currency: body.currency,
    lookbackDays: body.lookback_days,
    tmtAttributed: body.tmt_attributed,
    spendSource: body.spend_source,
    rows: body.rows.map(attributionRowFromWire),
    totals: attributionMetricsFromWire(body.totals),
    totalsPrevious: body.totals_previous ? attributionMetricsFromWire(body.totals_previous) : null,
    totalSpendMinor: body.total_spend_minor,
    totalPreviousSpendMinor: body.total_previous_spend_minor,
    dataQuality: dataQualityFromWire(body.data_quality),
  }
}

/** The route's query string. No environment or website: the route is production only, across every website. */
export function attributionSearch(query: AttributionQuery): string {
  const params = new URLSearchParams({ from: query.from, to: query.to, model: query.model, group_by: query.groupBy })
  if (query.compare) params.set('compare', 'previous_period')
  return params.toString()
}
